/**
 * Image Preloader
 * 
 * Preloads commonly used item icons and images into the cache
 * to ensure instant loading for frequently accessed items.
 */

import ImageCacheManager from './ImageCacheManager';

export class ImagePreloader {
  private static instance: ImagePreloader;
  private isPreloading = false;
  private preloadedCategories = new Set<string>();

  private constructor() {}

  public static getInstance(): ImagePreloader {
    if (!ImagePreloader.instance) {
      ImagePreloader.instance = new ImagePreloader();
    }
    return ImagePreloader.instance;
  }

  /**
   * Get public URL
   */
  private getPublicUrl(): string {
    return process.env.PUBLIC_URL || '';
  }

  /**
   * Preload essential icons (always needed)
   */
  public async preloadEssentials(): Promise<void> {
    if (this.preloadedCategories.has('essentials')) return;
    
    const publicUrl = this.getPublicUrl();
    const essentialIcons = [
      `${publicUrl}/assets/unknown.png`,
      `${publicUrl}/assets/icon.png`,
      `${publicUrl}/assets/portraits/generic_contraband.png`,
    ];

    console.log('🔄 Preloading essential icons...');
    await ImageCacheManager.preload(essentialIcons);
    this.preloadedCategories.add('essentials');
    console.log('✅ Essential icons preloaded');
  }

  /**
   * Preload common item icons (frequently used items)
   */
  public async preloadCommonItems(): Promise<void> {
    if (this.preloadedCategories.has('common-items')) return;
    
    const publicUrl = this.getPublicUrl();
    const commonItems = [
      // Currency and basic resources
      `${publicUrl}/assets/items/currency/pieces.png`,
      `${publicUrl}/assets/items/resources/wood.png`,
      `${publicUrl}/assets/items/resources/iron_ore.png`,
      `${publicUrl}/assets/items/resources/cloth.png`,
      
      // Common consumables
      `${publicUrl}/assets/items/consumables/health_potion.png`,
      `${publicUrl}/assets/items/consumables/stamina_potion.png`,
      `${publicUrl}/assets/items/food/fish.png`,
      `${publicUrl}/assets/items/food/bread.png`,
      
      // Basic equipment
      `${publicUrl}/assets/items/weapons/wooden_sword.png`,
      `${publicUrl}/assets/items/armor/leather_armor.png`,
    ];

    console.log('🔄 Preloading common item icons...');
    await ImageCacheManager.preload(commonItems);
    this.preloadedCategories.add('common-items');
    console.log('✅ Common item icons preloaded');
  }

  /**
   * Preload all portraits
   */
  public async preloadPortraits(): Promise<void> {
    if (this.preloadedCategories.has('portraits')) return;
    
    const publicUrl = this.getPublicUrl();
    
    // Generate portrait URLs (based on your project structure)
    const portraits: string[] = [];
    const portraitNames = [
      'generic_contraband',
      'merchant',
      'pirate',
      'navy',
      'civilian',
      // Add more as needed
    ];

    portraitNames.forEach(name => {
      portraits.push(`${publicUrl}/assets/portraits/${name}.png`);
    });

    console.log('🔄 Preloading portraits...');
    await ImageCacheManager.preload(portraits);
    this.preloadedCategories.add('portraits');
    console.log('✅ Portraits preloaded');
  }

  /**
   * Preload item category
   */
  public async preloadCategory(category: string, itemIds: string[]): Promise<void> {
    const cacheKey = `category-${category}`;
    if (this.preloadedCategories.has(cacheKey)) return;
    
    const publicUrl = this.getPublicUrl();
    const urls = itemIds.map(id => `${publicUrl}/assets/items/${category}/${id}.png`);

    console.log(`🔄 Preloading ${category} icons (${urls.length} items)...`);
    await ImageCacheManager.preload(urls);
    this.preloadedCategories.add(cacheKey);
    console.log(`✅ ${category} icons preloaded`);
  }

  /**
   * Preload inventory items (based on player's current inventory)
   */
  public async preloadInventoryItems(inventory: Array<{ itemId: string; icon?: string }>): Promise<void> {
    const publicUrl = this.getPublicUrl();
    const urls: string[] = [];

    inventory.forEach(item => {
      if (item.icon) {
        // Handle different icon formats
        if (item.icon.startsWith('http://') || item.icon.startsWith('https://')) {
          urls.push(item.icon);
        } else if (item.icon.startsWith('/assets')) {
          urls.push(`${publicUrl}${item.icon}`);
        } else {
          urls.push(`${publicUrl}/assets/${item.icon}`);
        }
      }
    });

    if (urls.length > 0) {
      console.log(`🔄 Preloading ${urls.length} inventory icons...`);
      await ImageCacheManager.preload(urls);
      console.log('✅ Inventory icons preloaded');
    }
  }

  /**
   * Preload shop items
   */
  public async preloadShopItems(shopItems: Array<{ icon?: string }>): Promise<void> {
    const publicUrl = this.getPublicUrl();
    const urls: string[] = [];

    shopItems.forEach(item => {
      if (item.icon) {
        if (item.icon.startsWith('http://') || item.icon.startsWith('https://')) {
          urls.push(item.icon);
        } else if (item.icon.startsWith('/assets')) {
          urls.push(`${publicUrl}${item.icon}`);
        } else {
          urls.push(`${publicUrl}/assets/${item.icon}`);
        }
      }
    });

    if (urls.length > 0) {
      console.log(`🔄 Preloading ${urls.length} shop icons...`);
      await ImageCacheManager.preload(urls);
      console.log('✅ Shop icons preloaded');
    }
  }

  /**
   * Preload all common assets (run on app initialization)
   */
  public async preloadAll(): Promise<void> {
    if (this.isPreloading) {
      console.log('⏳ Preloading already in progress...');
      return;
    }

    this.isPreloading = true;
    console.log('🚀 Starting comprehensive image preload...');

    try {
      // Preload in priority order
      await this.preloadEssentials();
      await this.preloadCommonItems();
      
      // Preload portraits in background (lower priority)
      this.preloadPortraits().catch(err => 
        console.warn('Failed to preload portraits:', err)
      );
      
      console.log('✅ Image preloading complete');
    } catch (error) {
      console.error('Error during image preloading:', error);
    } finally {
      this.isPreloading = false;
    }
  }

  /**
   * Reset preload tracking (for testing)
   */
  public reset(): void {
    this.preloadedCategories.clear();
    this.isPreloading = false;
  }

  /**
   * Check if category is preloaded
   */
  public isPreloaded(category: string): boolean {
    return this.preloadedCategories.has(category);
  }
}

// Export singleton instance
const imagePreloader = ImagePreloader.getInstance();
export default imagePreloader;






